import React, { useState, useEffect } from "react";
import { OverlayTrigger, Tooltip, Pagination } from "react-bootstrap";
import "../../asset/css/styles.css";
import { useDrop, useDrag, DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import {
  getAllStory,
  updateStoryPoints,
  updateStoryStatus,
  sendForRTS,
} from "../../pages/api/adminDashboard";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import TaskDetails from "../activesprint/taskdetails/page";

/* ── Badge helpers ─────────────────────────────────────────────────────────── */
const PRIORITY_STYLES = {
  HIGH: { background: "#fdecea", color: "#e53935" },
  MEDIUM: { background: "#fff3e0", color: "#fb8c00" },
  LOW: { background: "#e8f5e9", color: "#43a047" },
};

const TYPE_STYLES = {
  feature: { background: "#e3f0ff", color: "#1565c0" },
  bug: { background: "#fdecea", color: "#c62828" },
  task: { background: "#e8f5e9", color: "#2e7d32" },
  enhancement: { background: "#fff3e0", color: "#e65100" },
  report: { background: "#f3e5f5", color: "#6a1b9a" },
};

const getTypeBadgeStyle = (type) =>
  TYPE_STYLES[type?.toLowerCase()] || { background: "#e0e0e0", color: "#555" };

const PriorityBadge = ({ priority }) => {
  if (!priority) return null;
  const style = PRIORITY_STYLES[priority] || { background: "#e0e0e0", color: "#aaa" };
  return (
    <span className="badge-base" style={style}>
      {priority.charAt(0) + priority.slice(1).toLowerCase()}
    </span>
  );
};

const TypeBadge = ({ storyType }) => {
  if (!storyType) return null;
  return (
    <span className="badge-base" style={getTypeBadgeStyle(storyType)}>
      {storyType.charAt(0) + storyType.slice(1).toLowerCase()}
    </span>
  );
};

const SprintProgressBar = ({ progress = 0 }) => (
  <div className="as-progress-wrap">
    <div className="flex-between txt-12 mb-1">
      <span className="as-progress-label">Sprint progress</span>
      <span className="as-progress-pct">{progress}%</span>
    </div>
    <div className="as-progress-track">
      <div className="as-progress-bar" style={{ width: `${progress}%` }} />
    </div>
  </div>
);

const DropCell = ({ status, storyId, onDrop, className, children }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ["STORY"],
    drop: (item) => onDrop(status, item.storyId),
    collect: (monitor) => ({ isOver: !!monitor.isOver() }),
  }));

  const hoverBg = isOver
    ? status === "TODO"
      ? "#e1e5eb"
      : status === "INPROGRESS"
        ? "#d9e1f9"
        : status === "INTESTING"
          ? "#f3e8ff"
          : "#d4f7de"
    : undefined;

  return (
    <div
      ref={drop}
      className={`${className} scroll-thin`}
      style={{ minHeight: "100px", transition: "background 0.2s", backgroundColor: hoverBg }}
    >
      {children}
    </div>
  );
};

const DraggableStoryCard = ({ story, canDrag, children }) => {
  const isLocked = !canDrag || story.sentForRTS || story.rtsAccepted;

  const [{ isDragging }, dragRef] = useDrag(
    () => ({
      type: "STORY",
      item: { type: "STORY", storyId: story.id },
      canDrag: () => !isLocked,
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [story.id, isLocked],
  );

  return (
    <div
      ref={dragRef}
      style={{
        background: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: "8px",
        padding: "8px 10px",
        marginBottom: "8px",
        cursor: isLocked ? "default" : "grab",
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      {children}
    </div>
  );
};

const StoryCard = ({ story, onView, isMine, editablePoints, onEditPoints, onTyping, onSave }) => (
  <>
    <div className="as-card-top-row">
      <span className="as-card-id">#{story.id}</span>
      <div className="as-card-badges">
        <TypeBadge storyType={story.storyType} />
        {isMine && (
          <span className="badge-base" style={{ background: "#eef2ff", color: "#4338ca" }}>
            Mine
          </span>
        )}
      </div>
    </div>

    <div className="as-card-title">{story.storyTitle}</div>

    {story.storyDescription && (
      <div className="as-card-desc">
        {story.storyDescription.replace(/[#*_`>\[\]]/g, "").trim()}
      </div>
    )}

    <div className="as-card-bottom-row">
      <div className="as-card-bottom-left">
        <PriorityBadge priority={story.priority} />

        <span className="issue-card-sp">
          {editablePoints && isMine ? (
            story.editPoints ? (
              <input
                type="number"
                step="0.5"
                value={story.tempPoints}
                onChange={(e) => onTyping(story.id, e.target.value)}
                onBlur={() => onSave(story.id)}
                onKeyDown={(e) => e.key === "Enter" && onSave(story.id)}
                autoFocus
                className="as-sp-input"
              />
            ) : (
              <>
                <span className="as-sp-click" onClick={() => onEditPoints(story.id, story.storyPoints)}>
                  {`${story.storyPoints != null ? parseFloat(story.storyPoints).toFixed(1).replace(/\.0$/, "") : "-"} SP`}
                </span>
                {story.testStoryPoints != null && Number(story.testStoryPoints) > 0 && (
                  <span className="as-sp-click" style={{ marginLeft: 4, cursor: "default" }}>
                    {`/ ${parseFloat(story.testStoryPoints).toFixed(1).replace(/\.0$/, "")} SP`}
                  </span>
                )}
              </>
            )
          ) : (
            <>
              <span>{story.storyPoints ? `${story.storyPoints} SP` : "- SP"}</span>
              {story.testStoryPoints != null && Number(story.testStoryPoints) > 0 && (
                <span style={{ marginLeft: 4 }}>{`/ ${story.testStoryPoints} SP`}</span>
              )}
            </>
          )}
        </span>
      </div>

      <div className="as-card-bottom-right">
        {story.storyUsers && (
          <div className="issue-card-avatars">
            {story.storyUsers.filter((su) => su.user?.name).map((su) => (
              <OverlayTrigger key={su.id} placement="top" overlay={<Tooltip>{su.user.name}</Tooltip>}>
                <div className="issue-avatar">{getInitials(su.user.name)}</div>
              </OverlayTrigger>
            ))}
          </div>
        )}
        
        <OverlayTrigger placement="top" overlay={<Tooltip>View task</Tooltip>}>
          <button className="issue-card-icon-btn" onClick={() => onView(story)}>
            <i className="bi bi-eye" />
          </button>
        </OverlayTrigger>
      </div>
    </div>

    <div className="as-card-meta-row">
      {story.sprint?.startDate && (
        <span className="as-card-date">
          <i className="bi bi-calendar3 txt-11" />
          {formatDate(story.sprint.startDate)}
        </span>
      )}
      <div className="as-card-meta-right">
  {((story.commentCount ?? story.comments?.length ?? 0) > 0) && (
    <span className="as-card-meta-item">
      <i className="bi bi-chat txt-11" />
      {story.commentCount ?? story.comments?.length ?? 0}
    </span>
  )}
  {story.attachments?.length > 0 && (
    <span className="as-card-meta-item">
      <i className="bi bi-paperclip txt-11" />
      {story.attachments.length}
    </span>
  )}
</div>
    </div>

    {story.sentForRTS && !story.rtsAccepted && <div className="rts-bar sent">Sent for RTS</div>}
    {story.rtsAccepted && <div className="rts-bar accepted">✓ Accepted</div>}
  </>
);

const getInitials = (name) => {
  if (!name) return "";
  const parts = name.trim().split(" ");
  return parts.length === 1
    ? parts[0][0].toUpperCase()
    : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });

const CustomPagination = ({ currentPage, totalPages, onPageChange }) => {
  const maxVisible = 3;
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
  const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);

  return (
    <Pagination className="justify-content-center mt-3">
      <Pagination.First onClick={() => onPageChange(1)} disabled={currentPage === 1} />
      <Pagination.Prev onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} />
      {start > 1 && (
        <>
          <Pagination.Item onClick={() => onPageChange(1)}>1</Pagination.Item>
          {start > 2 && <Pagination.Ellipsis />}
        </>
      )}
      {pages.map((p) => (
        <Pagination.Item key={p} active={p === currentPage} onClick={() => onPageChange(p)}>
          {p}
        </Pagination.Item>
      ))}
      {end < totalPages && (
        <>
          {end < totalPages - 1 && <Pagination.Ellipsis />}
          <Pagination.Item onClick={() => onPageChange(totalPages)}>{totalPages}</Pagination.Item>
        </>
      )}
      <Pagination.Next onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} />
      <Pagination.Last onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages} />
    </Pagination>
  );
};

/* ════════════════════════════════════════════════════════════════════════════
   EMPLOYEE ACTIVE SPRINT - SHOWS ONLY ASSIGNED TASKS
   ════════════════════════════════════════════════════════════════════════════ */
const EmployeeActiveSprint = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [showViewTaskModal, setShowViewTaskModal] = useState(false);
  const [stories, setStories] = useState([]);
  const [currentStory, setCurrentStory] = useState(null);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("token");
  const currentUserId = localStorage.getItem("id");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");
  const [sprintProgress, setSprintProgress] = useState(0);
  const [isSendingRTS, setIsSendingRTS] = useState(false);

  const [selectedSprint, setSelectedSprint] = useState(() => {
    const s = localStorage.getItem("selectedSprint");
    return s ? JSON.parse(s) : null;
  });

  /* ✅ CHECK IF STORY IS ASSIGNED TO CURRENT EMPLOYEE */
  const isMine = (story) =>
    !!story?.storyUsers?.some((su) => String(su.user?.id) === String(currentUserId));

  useEffect(() => {
    const sync = () => {
      const s = localStorage.getItem("selectedSprint");
      setSelectedSprint(s ? JSON.parse(s) : null);
    };
    window.addEventListener("sprintChanged", sync);
    return () => window.removeEventListener("sprintChanged", sync);
  }, []);
  useEffect(() => {
  const sync = () => {
    const s = localStorage.getItem("selectedSprint");
    setSelectedSprint(s ? JSON.parse(s) : null);
  };
  window.addEventListener("sprintChanged", sync);
  return () => window.removeEventListener("sprintChanged", sync);
}, []);

// ✅ ADD THIS RIGHT HERE
useEffect(() => {
  const handleCommentCount = (e) => {
    const { storyId, commentCount } = e.detail;
    setStories((prev) =>
      prev.map((s) => s.id === storyId ? { ...s, commentCount } : s)
    );
  };
  window.addEventListener("commentCountUpdated", handleCommentCount);
  return () => window.removeEventListener("commentCountUpdated", handleCommentCount);
}, []);

  /* ✅ FETCH ALL STORIES AND AUTO-SELECT ACTIVE SPRINT (SAME AS ADMIN) */
  useEffect(() => {
    const fetchStories = async () => {
      setLoading(true);
      try {
            localStorage.removeItem("selectedSprint");

        const response = await getAllStory(token);
        const allStories = response.data.sort((a, b) => b.id - a.id);
        setStories(allStories);

        // ✅ USE SAME LOGIC AS ADMIN: Find first story with ACTIVE sprint
       const savedSprint = localStorage.getItem("selectedSprint");
if (!savedSprint) {
  const active = allStories.find((s) => s.sprint && s.sprint.sprintStatus === "ACTIVE");
  if (active?.sprint) {
    setSelectedSprint(active.sprint);
    localStorage.setItem("selectedSprint", JSON.stringify(active.sprint));
    window.dispatchEvent(new Event("sprintChanged"));
  } else {
    // Fallback: fetch sprints directly (same as admin side)
    const { getAllSprints } = await import("../../pages/api/adminDashboard");
    const sprintRes = await getAllSprints(token);
    const activeSprint = sprintRes.data?.find(s => s.sprintStatus === "ACTIVE");
    if (activeSprint) {
      setSelectedSprint(activeSprint);
      localStorage.setItem("selectedSprint", JSON.stringify(activeSprint));
      window.dispatchEvent(new Event("sprintChanged"));
    }
  }
}
      } catch (err) {
        console.error("Error fetching stories:", err);
        toast.error("Failed to load stories");
      } finally {
        setLoading(false);
      }
    };
    if (token) {
      fetchStories();
    }
  }, [token]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedSprint]);

  const handleDrop = async (newStatus, storyId) => {
    const story = stories.find((s) => s.id === storyId);
    if (!story) return;

    if (!isMine(story)) {
      toast.error("You can only update tasks assigned to you");
      return;
    }
    if (story.sentForRTS || story.rtsAccepted) {
      toast.error(story.rtsAccepted ? "RTS already accepted. Cannot move story." : "RTS sent. Cannot move story.");
      return;
    }

    try {
      await updateStoryStatus(storyId, newStatus, token);
      setStories((prev) => prev.map((s) => (s.id === storyId ? { ...s, status: newStatus } : s)));
      const label =
        newStatus === "INPROGRESS"
          ? "In Development"
          : newStatus === "INTESTING"
            ? "In Testing"
            : newStatus === "COMPLETED"
              ? "Completed"
              : newStatus;
      toast.success(`Story moved to ${label} successfully!`);
    } catch (e) {
      console.error(e);
      toast.error("Failed to update story status");
    }
  };

  const handleViewTaskShow = (story) => {
    setCurrentStory({ ...story, attachments: story.attachments || [] });
    setShowViewTaskModal(true);
  };

  const enableStoryPointEdit = (id, val) =>
    setStories((prev) =>
      prev.map((s) => (s.id === id ? { ...s, editPoints: true, tempPoints: val != null ? val.toString() : "" } : s)),
    );

  const handleStoryPointTyping = (id, val) =>
    setStories((prev) => prev.map((s) => (s.id === id ? { ...s, tempPoints: val } : s)));

  const saveStoryPoints = async (id) => {
    const story = stories.find((s) => s.id === id);
    const pts = parseFloat(story.tempPoints);
    if (isNaN(pts)) {
      toast.error("Please enter a valid number");
      setStories((prev) => prev.map((s) => (s.id === id ? { ...s, editPoints: false } : s)));
      return;
    }
    try {
      await updateStoryPoints(id, pts, token);
      setStories((prev) =>
        prev.map((s) => (s.id === id ? { ...s, storyPoints: pts, editPoints: false, tempPoints: undefined } : s)),
      );
    } catch {
      setStories((prev) => prev.map((s) => (s.id === id ? { ...s, editPoints: false } : s)));
    }
  };

  const handleSendRTS = async (story) => {
  if (!story || isSendingRTS) return;
  if (!isMine(story)) {
    toast.error("You can only send your own tasks for RTS");
    return;
  }
  if (story.status !== "COMPLETED") {
    toast.error("Task must be Completed before sending for RTS");
    return;
  }
  if (!story.prLink || !story.prLink.trim() || story.prLink === "null") {
    toast.error("Please add a PR Link before sending for RTS");
    return;
  }
   try {
      setIsSendingRTS(true);
      await sendForRTS(story.id, token);
      toast.success("Sent for RTS successfully!");
      setStories((prev) => prev.map((s) => (s.id === story.id ? { ...s, sentForRTS: true, canDrag: false } : s)));
      setCurrentStory((prev) => (prev ? { ...prev, sentForRTS: true, canDrag: false } : prev));
    } catch (e) {
      console.error(e);
      if (e?.response?.status === 429) {
        toast.warning("Email limit reached for sending RTS. Your request was still submitted.");
        setStories((prev) => prev.map((s) => (s.id === story.id ? { ...s, sentForRTS: true, canDrag: false } : s)));
        setCurrentStory((prev) => (prev ? { ...prev, sentForRTS: true, canDrag: false } : prev));
      } else {
        toast.error("Failed to send for RTS");
      }
    } finally {
      setIsSendingRTS(false);
    }
  };

  /* ✅ FILTER: ONLY SHOW STORIES ASSIGNED TO ME IN ACTIVE SPRINT */
 const filteredStories = stories
    .filter((story) => {
      if (!story.sprint) return false;
      if (story.sprint.startDate === null && story.sprint.endDate === null) return false;
      if (story.sprint.sprintName?.toLowerCase() === "backlog") return false;
      if (story.sprint.sprintStatus === "COMPLETED") return false;

      // ✅ A task's sprint must actually be started (ACTIVE) before it can
      // show here — checked unconditionally, even when a sprint is selected.
      // Previously this was only checked in the "no selectedSprint" branch,
      // so a task moved into a PLANNED sprint still showed up here if that
      // sprint happened to be the cached selectedSprint.
      if (story.sprint.sprintStatus !== "ACTIVE") return false;
      if (selectedSprint) {
        const sprintMatch = Number(story.sprint.id) === Number(selectedSprint.id);
        if (!sprintMatch) return false;
      }

      // ✅ CRITICAL: Check if assigned to me
      return isMine(story);
    })
    .filter((story) => {
      if (!searchTerm.trim()) return true;
      const t = searchTerm.toLowerCase();
      return (
        story.storyTitle?.toLowerCase().includes(t) ||
        story.storyDescription?.toLowerCase().includes(t) ||
        story.status?.toLowerCase().includes(t)
      );
    });

  const totalPages = Math.ceil(filteredStories.length / itemsPerPage);
  const indexOfLastStory = currentPage * itemsPerPage;
  const indexOfFirstStory = indexOfLastStory - itemsPerPage;
  const currentStories = filteredStories.slice(indexOfFirstStory, indexOfLastStory);

  useEffect(() => {
    if (!filteredStories.length) {
      setSprintProgress(0);
      return;
    }
    const completed = filteredStories.filter((s) => s.status === "COMPLETED").length;
    setSprintProgress(Math.round((completed / filteredStories.length) * 100));
  }, [filteredStories, selectedSprint?.id]);

  const sprintEndDate = selectedSprint ? new Date(selectedSprint.endDate) : null;
  const daysLeft = sprintEndDate ? Math.ceil((sprintEndDate - new Date()) / 864e5) : null;

  const currentlyMine = isMine(currentStory);

  return (
    <DndProvider backend={HTML5Backend}>
      <h5 className="as-heading">Active Sprint</h5>

      <div className="flex-between mb-1 as-toolbar">
        <div className="flex-center" style={{ gap: 8 }}>
          <span>Show:</span>
          <select
            className="form-select form-select-sm d-inline-block w-auto"
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(parseInt(e.target.value));
              setCurrentPage(1);
            }}
          >
            {[3, 5, 10, 20].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <span>stories per page</span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div className="input-group input-group-sm as-search-group">
            <span className="input-group-text as-search-icon-wrap">
              <i className="bi bi-search txt-11" />
            </span>
            <input
              type="text"
              className="form-control as-search-input"
              placeholder="Search Tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="btn btn-outline-secondary" onClick={() => setSearchTerm("")}>
                <i className="bi bi-x" />
              </button>
            )}
          </div>
        </div>
      </div>

      {selectedSprint && selectedSprint.sprintStatus === "ACTIVE" && (
        <div className="as-sprint-card flex-between" style={{ gap: 24, marginBottom: 4 }}>
          <div className="as-sprint-left">
            <div className="flex-gap-10" style={{ marginBottom: 4 }}>
              <span className="as-sprint-name">{selectedSprint.sprintName}</span>
              {daysLeft != null && daysLeft >= 0 && <span className="as-sprint-active-badge">Active</span>}
            </div>
            <div className="flex-gap-12 txt-12">
              <span className="as-sprint-dates">
                {formatDate(selectedSprint.startDate)} – {formatDate(selectedSprint.endDate)}
              </span>
              {daysLeft != null && daysLeft >= 0 ? (
                <span className="flex-gap-4">
                  <i className="bi bi-clock txt-12 txt-grey" />
                  <span className="as-sprint-days-text">
                    {daysLeft} {daysLeft === 1 ? "day" : "days"} left
                  </span>
                </span>
              ) : daysLeft != null ? (
                <span className="as-sprint-ended">
                  Overdue by {Math.abs(daysLeft)} {Math.abs(daysLeft) === 1 ? "day" : "days"}
                </span>
              ) : null}
            </div>
          </div>
          <SprintProgressBar progress={sprintProgress} />
        </div>
      )}

      <div className="dashboard-container">
        <div className="board-wrap">
          <div className="board-header">
            {[
              { label: "To Do", dot: "#9CA3AF", status: "TODO" },
              { label: "In Development", dot: "#3B82F6", status: "INPROGRESS" },
              { label: "In Testing", dot: "#A855F7", status: "INTESTING" },
              { label: "Completed", dot: "#22C55E", status: "COMPLETED" },
            ].map(({ label, dot, status }) => (
              <div key={status} className="board-header-cell">
                <div className="board-header-left">
                  <span className="board-dot" style={{ background: dot }} />
                  <span className="board-col-title">{label}</span>
                  <span className="board-col-count">{currentStories.filter((s) => s.status === status).length}</span>
                </div>
              </div>
            ))}
          </div>

        {loading ? (
  <div className="as-board-empty">Loading...</div>
) : !selectedSprint || selectedSprint.sprintStatus !== "ACTIVE" ? (
  <div
    className="as-board-empty"
    style={{ fontSize: "22px", fontWeight: 700, color: "#374151", padding: "48px 0" }}
  >
    No active sprint available.
  </div>
) : currentStories.length === 0 ? (
  <div className="as-board-empty">{searchTerm ? "No matching stories found" : "No stories assigned to you"}</div>
) : (
            currentStories.map((story) => {
              const effectiveStatus = !story.status || !["TODO", "INPROGRESS", "INTESTING", "COMPLETED"].includes(story.status)
                ? "TODO"
                : story.status;
              const mine = isMine(story);

              return (
                <div key={story.id} className="board-row">
                  <DropCell status="TODO" onDrop={handleDrop} storyId={story.id} className="subtask-col-cell todo">
                    {effectiveStatus === "TODO" && (
                      <DraggableStoryCard story={story} canDrag={mine}>
                        <StoryCard
                          story={story}
                          onView={handleViewTaskShow}
                          isMine={mine}
                          editablePoints
                          onEditPoints={enableStoryPointEdit}
                          onTyping={handleStoryPointTyping}
                          onSave={saveStoryPoints}
                        />
                      </DraggableStoryCard>
                    )}
                  </DropCell>

                  <DropCell status="INPROGRESS" onDrop={handleDrop} storyId={story.id} className="subtask-col-cell inprogress">
                    {effectiveStatus === "INPROGRESS" && (
                      <DraggableStoryCard story={story} canDrag={mine}>
                        <StoryCard story={story} onView={handleViewTaskShow} isMine={mine} />
                      </DraggableStoryCard>
                    )}
                  </DropCell>

                  <DropCell status="INTESTING" onDrop={handleDrop} storyId={story.id} className="subtask-col-cell intesting">
                    {effectiveStatus === "INTESTING" && (
                      <DraggableStoryCard story={story} canDrag={mine}>
                        <StoryCard story={story} onView={handleViewTaskShow} isMine={mine} />
                      </DraggableStoryCard>
                    )}
                  </DropCell>

                  <DropCell status="COMPLETED" onDrop={handleDrop} storyId={story.id} className="subtask-col-cell done">
                    {effectiveStatus === "COMPLETED" && (
                      <DraggableStoryCard story={story} canDrag={mine}>
                        <StoryCard story={story} onView={handleViewTaskShow} isMine={mine} />
                      </DraggableStoryCard>
                    )}
                  </DropCell>
                </div>
              );
            })
          )}
        </div>

        <div className="paginationBottom">
          {filteredStories.length > 0 && (
            <CustomPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
          )}
         
        </div>

        <TaskDetails
          showViewTaskModal={showViewTaskModal}
          setShowViewTaskModal={setShowViewTaskModal}
          currentStory={currentStory}
          setCurrentStory={setCurrentStory}
         onStoryUpdated={async () => {
  try {
    const response = await getAllStory(token);
    setStories(response.data.sort((a, b) => b.id - a.id));
  } catch (e) {
    console.error("Failed to refresh stories", e);
  }
}}
          readOnly={true}
          showSendRTS={currentlyMine}
          onSendRTS={handleSendRTS}
          isSendingRTS={isSendingRTS}
        />

        <ToastContainer />
      </div>
    </DndProvider>
  );
};

export default EmployeeActiveSprint;